import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Info, Columns3, Share2 } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";

export const Route = createFileRoute("/_authenticated/admin/footer")({
  component: FooterAdmin,
});

export type FooterLink = { label: string; to: string };
export type FooterColumn = { title: string; links: FooterLink[] };
export type FooterSocial = { kind: string; url: string };
export type FooterSettings = {
  tagline: string;
  email: string;
  phone: string;
  address: string;
  columns: FooterColumn[];
  socials: FooterSocial[];
  copyright: string;
};

const DEFAULT: FooterSettings = {
  tagline:
    "CoderLity is a full-service digital agency building software, SaaS, ERP, POS and growth systems for ambitious brands worldwide.",
  email: "hello@coderlity.com",
  phone: "+966 55 975 8082",
  address: "Riyadh & Jeddah, KSA · CO Bazar, Rangpur, BD",
  columns: [
    { title: "Services", links: [{ label: "Web Development", to: "/services" }] },
    { title: "Company", links: [{ label: "About", to: "/about" }, { label: "Contact", to: "/contact" }] },
    { title: "Legal", links: [{ label: "Privacy Policy", to: "/privacy" }, { label: "Terms", to: "/terms" }] },
  ],
  socials: [
    { kind: "Twitter", url: "#" },
    { kind: "LinkedIn", url: "#" },
    { kind: "GitHub", url: "#" },
    { kind: "Facebook", url: "#" },
    { kind: "Instagram", url: "#" },
  ],
  copyright: "© 2022–{year} All Rights Reserved · Developed by CoderLity",
};

const SOCIAL_KINDS = ["Twitter", "LinkedIn", "GitHub", "Facebook", "Instagram", "YouTube", "TikTok", "WhatsApp"];

function FooterAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["site_settings", "footer"],
    queryFn: async (): Promise<FooterSettings> => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "footer")
        .maybeSingle();
      return { ...DEFAULT, ...((data?.value as FooterSettings) ?? {}) };
    },
  });

  const [state, setState] = useState<FooterSettings>(DEFAULT);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data) {
      setState(data);
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (value: FooterSettings) => {
      const { error } = await supabase.from("site_settings").upsert({ key: "footer", value });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Footer saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["site_settings", "footer"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const upd = (next: FooterSettings) => {
    setState(next);
    setDirty(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <AdminPageHeader
          title="Footer"
          description="Manage footer columns, contact details, social links, and copyright text."
        />
        <div className="flex gap-2">
          <Button variant="ghost" disabled={!dirty || !data} onClick={() => data && (setState(data), setDirty(false))}>
            <RotateCcw className="h-4 w-4" /> Revert
          </Button>
          <Button onClick={() => save.mutate(state)} disabled={!dirty || save.isPending}>
            <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="general"><Info className="mr-1.5 h-4 w-4" />General</PillTabsTrigger>
          <PillTabsTrigger value="columns"><Columns3 className="mr-1.5 h-4 w-4" />Columns</PillTabsTrigger>
          <PillTabsTrigger value="socials"><Share2 className="mr-1.5 h-4 w-4" />Social links</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="general" className="mt-4">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" value={state.email} onChange={(v) => upd({ ...state, email: v })} />
                <Field label="Phone" value={state.phone} onChange={(v) => upd({ ...state, phone: v })} />
                <Field label="Address" value={state.address} onChange={(v) => upd({ ...state, address: v })} />
                <Field label="Copyright (use {year} for current year)" value={state.copyright} onChange={(v) => upd({ ...state, copyright: v })} />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Tagline</Label>
                <Textarea rows={3} value={state.tagline} onChange={(e) => upd({ ...state, tagline: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="columns" className="mt-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Columns</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    upd({ ...state, columns: [...state.columns, { title: "New column", links: [] }] })
                  }
                >
                  <Plus className="h-4 w-4" /> Add column
                </Button>
              </div>

              {state.columns.map((col, ci) => (
                <div key={ci} className="rounded-md border border-border bg-background/40 p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      className="max-w-xs"
                      value={col.title}
                      onChange={(e) => {
                        const cols = [...state.columns];
                        cols[ci] = { ...col, title: e.target.value };
                        upd({ ...state, columns: cols });
                      }}
                      placeholder="Column title"
                    />
                    <div className="ml-auto flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (ci === 0) return;
                        const cols = [...state.columns];
                        [cols[ci - 1], cols[ci]] = [cols[ci], cols[ci - 1]];
                        upd({ ...state, columns: cols });
                      }}><ArrowUp className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (ci >= state.columns.length - 1) return;
                        const cols = [...state.columns];
                        [cols[ci + 1], cols[ci]] = [cols[ci], cols[ci + 1]];
                        upd({ ...state, columns: cols });
                      }}><ArrowDown className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (!confirm(`Delete column "${col.title}"?`)) return;
                        upd({ ...state, columns: state.columns.filter((_, k) => k !== ci) });
                      }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {col.links.map((lnk, li) => (
                      <div key={li} className="grid gap-2 sm:grid-cols-[1fr_1.5fr_auto] items-center">
                        <Input
                          value={lnk.label}
                          placeholder="Label"
                          onChange={(e) => {
                            const links = [...col.links];
                            links[li] = { ...lnk, label: e.target.value };
                            const cols = [...state.columns];
                            cols[ci] = { ...col, links };
                            upd({ ...state, columns: cols });
                          }}
                        />
                        <Input
                          value={lnk.to}
                          placeholder="/path"
                          onChange={(e) => {
                            const links = [...col.links];
                            links[li] = { ...lnk, to: e.target.value };
                            const cols = [...state.columns];
                            cols[ci] = { ...col, links };
                            upd({ ...state, columns: cols });
                          }}
                        />
                        <Button size="sm" variant="ghost" onClick={() => {
                          const links = col.links.filter((_, k) => k !== li);
                          const cols = [...state.columns];
                          cols[ci] = { ...col, links };
                          upd({ ...state, columns: cols });
                        }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const cols = [...state.columns];
                        cols[ci] = { ...col, links: [...col.links, { label: "New link", to: "/" }] };
                        upd({ ...state, columns: cols });
                      }}
                    >
                      <Plus className="h-4 w-4" /> Add link
                    </Button>
                  </div>
                </div>
              ))}
              {state.columns.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No columns yet. Click “Add column” to create the first one.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="socials" className="mt-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Social links</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => upd({ ...state, socials: [...state.socials, { kind: "Twitter", url: "" }] })}
                >
                  <Plus className="h-4 w-4" /> Add social
                </Button>
              </div>
              {state.socials.map((s, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[180px_1fr_auto] items-center">
                  <select
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    value={s.kind}
                    onChange={(e) => {
                      const socials = [...state.socials];
                      socials[i] = { ...s, kind: e.target.value };
                      upd({ ...state, socials });
                    }}
                  >
                    {SOCIAL_KINDS.map((k) => <option key={k}>{k}</option>)}
                  </select>
                  <Input
                    value={s.url}
                    placeholder="https://…"
                    onChange={(e) => {
                      const socials = [...state.socials];
                      socials[i] = { ...s, url: e.target.value };
                      upd({ ...state, socials });
                    }}
                  />
                  <Button size="sm" variant="ghost" onClick={() => upd({ ...state, socials: state.socials.filter((_, k) => k !== i) })}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {state.socials.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No social links yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
